import { Payload } from ".";
import { AppDispatch } from "../../configureStore";
import { ConfigStepId, SignatureStatus } from "../../utils/configuration";
import execute from "../http/execute";
import failure from "../http/failure";
import { get, patch, post } from "../http/request";
import success from "../http/success";

export interface UserSteps {
    id: ConfigStepId;
    completed: boolean;
}

export interface Applicant {
    id: string;
    applicantId: string;
    cId?: string;
    steps: UserSteps[];
    missingInfo: PII<boolean>;
    name?: string;
}

export interface Tokens {
    refreshToken?: string;
    accessToken: string;
}

export interface SigSignSignature {
    completed: boolean;
    status: SignatureStatus;
    embedUrl: string;
    transactionId: string;
    document: {
        id: string;
        documentId: string;
        name: string;
        required: boolean;
        fields: {
            fieldId: string;
            populatedValue?: string;
            defaultValue?: string;
        }[];
    };
}

export interface BorrowerGuide {
    url: string;
    completed: boolean;
}

export interface InfoTranscript {
    type: ConfigStepId.transcript;
    info: { signatures: SigSignSignature[]; guide?: BorrowerGuide; } | SigSignSignature[];
}

export interface InfoTranscriptNew {
    type: ConfigStepId.transcript;
    info: { signatures: SigSignSignature[]; guide?: BorrowerGuide; };
}

export interface ApplicantBusiness {
    id: string;
    name?: string;
    ein?: string;
    email?: string;
    title?: string;
    address: {
        lineOne?: string;
        lineTwo?: string;
        city?: string;
        state?: string;
        postalCode?: string;
    };
    forms: { formName: string; taxYears: number; }[];
    signature: SigSignSignature[];
    completed: boolean;
}

export interface InfoBusiness {
    type: ConfigStepId.business;
    info: {
        businesses: ApplicantBusiness[];
        sharedSignature?: SigSignSignature;
    };
}

export type InfoSuccess = InfoTranscript | InfoBusiness;

const initialState: {
    applicant: Applicant | null;
    tokens?: Tokens;
    services: { [id: string]: any };
    transcript?: InfoTranscriptNew;
    business?: {
        businesses: ApplicantBusiness[];
        sharedSignature?: SigSignSignature;
    };
    currentStep: number;
    errorSteps: { [id: string]: boolean; };
} = {
    applicant: null,
    services: {},
    currentStep: 0,
    errorSteps: {},
}

export const SET_CURRENT_STEP = 'applicant/SET_CURRENT_STEP';

export const GET_APPLICANT_REQUEST = 'applicant/GET_APPLICANT_REQUEST';
export const GET_APPLICANT_SUCCESS = 'applicant/GET_APPLICANT_SUCCESS';
export const GET_APPLICANT_FAILURE = 'applicant/GET_APPLICANT_FAILURE';

export const CREATE_APPLICANT_REQUEST = 'applicant/CREATE_APPLICANT_REQUEST';
export const CREATE_APPLICANT_SUCCESS = 'applicant/CREATE_APPLICANT_SUCCESS';
export const CREATE_APPLICANT_FAILURE = 'applicant/CREATE_APPLICANT_FAILURE';

export const GET_SERVICE_INFO_REQUEST = 'applicant/GET_SERVICE_INFO_REQUEST';
export const GET_SERVICE_INFO_SUCCESS = 'applicant/GET_SERVICE_INFO_SUCCESS';
export const GET_SERVICE_INFO_FAILURE = 'applicant/GET_SERVICE_INFO_FAILURE';

export const GET_SIGNATURE_STATUS_REQUEST = 'applicant/GET_SIGNATURE_STATUS_REQUEST';
export const GET_SIGNATURE_STATUS_SUCCESS = 'applicant/GET_SIGNATURE_STATUS_SUCCESS';
export const GET_SIGNATURE_STATUS_FAILURE = 'applicant/GET_SIGNATURE_STATUS_FAILURE';

export const UPDATE_APPLICANT_REQUEST = 'applicant/UPDATE_APPLICANT_REQUEST';
export const UPDATE_APPLICANT_SUCCESS = 'applicant/UPDATE_APPLICANT_SUCCESS';
export const UPDATE_APPLICANT_FAILURE = 'applicant/UPDATE_APPLICANT_FAILURE';

export const ADD_BUSINESS_REQUEST = 'applicant/ADD_BUSINESS_REQUEST';
export const ADD_BUSINESS_SUCCESS = 'applicant/ADD_BUSINESS_SUCCESS';
export const ADD_BUSINESS_FAILURE = 'applicant/ADD_BUSINESS_FAILURE';

export const CHECK_BUSINESS_SIGNATURE_REQUEST = 'applicant/CHECK_BUSINESS_SIGNATURE_REQUEST';
export const CHECK_BUSINESS_SIGNATURE_SUCCESS = 'applicant/CHECK_BUSINESS_SIGNATURE_SUCCESS';
export const CHECK_BUSINESS_SIGNATURE_FAILURE = 'applicant/CHECK_BUSINESS_SIGNATURE_FAILURE';

export const CREATE_BUSINESS_SIGNATURE_REQUEST = 'applicant/CREATE_BUSINESS_SIGNATURE_REQUEST';
export const CREATE_BUSINESS_SIGNATURE_SUCCESS = 'applicant/CREATE_BUSINESS_SIGNATURE_SUCCESS';
export const CREATE_BUSINESS_SIGNATURE_FAILURE = 'applicant/CREATE_BUSINESS_SIGNATURE_FAILURE';

type ActionParams =
    | { type: typeof GET_APPLICANT_SUCCESS; data: { applicant: Applicant | null; createOnNull: boolean; tokens?: Tokens; }; }
    | { type: typeof CREATE_APPLICANT_SUCCESS; data: { applicant: Applicant; tokens?: Tokens; }; }
    | { type: typeof GET_SERVICE_INFO_SUCCESS; data: InfoSuccess; }
    | { type: typeof GET_SIGNATURE_STATUS_SUCCESS; data: { completed: boolean; transactionId?: string; applicant?: Applicant; status?: SignatureStatus; }; }
    | { type: typeof SET_CURRENT_STEP; newStep: number; }
    | { type: typeof UPDATE_APPLICANT_SUCCESS; data: { applicant: Applicant; }; step: ConfigStepId; input?: { step: ConfigStepId; }; }
    | { type: typeof ADD_BUSINESS_SUCCESS; data: { business: ApplicantBusiness; }; }
    | { type: typeof CHECK_BUSINESS_SIGNATURE_SUCCESS; data: { completed: boolean; transactionId?: string; status?: SignatureStatus; applicant?: Applicant; }; input?: { businessId?: string }; }
    | { type: typeof CREATE_BUSINESS_SIGNATURE_SUCCESS; data: { signature: SigSignSignature; businessIndex?: number; }; };

function reducer(state = initialState, action: ActionParams): typeof initialState {
    switch (action.type) {
        case SET_CURRENT_STEP:
            return {
                ...state,
                currentStep: action.newStep,
            }

        case GET_APPLICANT_SUCCESS:
        case CREATE_APPLICANT_SUCCESS:
            let currentStep = action.data.applicant ? action.data.applicant.steps.findIndex(({ completed }) => !completed) : -1;
            if (currentStep === -1) currentStep = action.data.applicant?.steps.length ?? -1;

            return {
                ...state,
                applicant: action.data.applicant,
                tokens: action.data.tokens,
                currentStep,
            };

        case GET_SERVICE_INFO_SUCCESS:
            switch (action.data.type) {
                case ConfigStepId.transcript:
                    if (Array.isArray(action.data.info)) {
                        const declined = action.data.info.some(({ status }) => status === 'Declined');

                        return {
                            ...state,
                            transcript: {
                                ...action.data,
                                info: {
                                    signatures: action.data.info,
                                }
                            },
                            errorSteps: {
                                ...state.errorSteps,
                                transcript: declined,
                            }
                        };
                    } else {
                        const declined = (action.data?.info?.signatures ?? []).some(({ status }) => status === 'Declined');

                        return {
                            ...state,
                            transcript: action.data as InfoTranscriptNew,
                            errorSteps: {
                                ...state.errorSteps,
                                transcript: declined,
                            }
                        };
                    }
                case ConfigStepId.business:
                    const businessDeclined = action.data.info.businesses.some(b =>
                        b.signature.some(s => s.status === 'Declined')
                    ) || action.data.info.sharedSignature?.status === 'Declined';

                    return {
                        ...state,
                        business: action.data.info,
                        errorSteps: {
                            ...state.errorSteps,
                            business: businessDeclined,
                        }
                    };
                default: return state;
            }

        case GET_SIGNATURE_STATUS_SUCCESS:
            if (!action.data.completed) {
                if (action.data.status === 'Declined') {
                    if (Array.isArray(state.transcript?.info)) {
                        const transcript = state.transcript ? {
                            ...state.transcript,
                            info: {
                                signatures: (state.transcript.info ?? []).map((info) => (info.transactionId === action.data.transactionId
                                    ? { ...info, status: action.data.status || 'Declined' }
                                    : info)
                                ),
                                guide: state.transcript.info.guide,
                            },
                        } : undefined;
                        return { ...state, transcript, errorSteps: { ...state.errorSteps, 'transcript': true, } }
                    } else {
                        const transcript = state.transcript ? {
                            ...state.transcript,
                            info: {
                                signatures: ((state.transcript.info.signatures) ?? []).map((info) => (info.transactionId === action.data.transactionId
                                    ? { ...info, status: action.data.status || 'Declined' }
                                    : info)
                                ),
                                guide: state.transcript.info.guide,
                            },
                        } : undefined;
                        return { ...state, transcript, errorSteps: { ...state.errorSteps, 'transcript': true, } }
                    }
                } else return state;
            }
            const transcript = state.transcript ? {
                ...state.transcript,
                info: {
                    signatures: (((state.transcript.info && Array.isArray(state.transcript?.info)) ? state.transcript.info : state.transcript.info.signatures) ?? []).map((info) => (info.transactionId === action.data.transactionId
                        ? { ...info, completed: true }
                        : info)
                    ),
                    guide: state.transcript.info.guide,
                }
            } : undefined;

            const completed = (transcript?.info?.signatures.every(({ completed }) => completed)) && Object.values(action.data.applicant?.missingInfo ?? {}).every((v) => !v) && (transcript.info.guide ? transcript.info.guide?.completed : true);
            return {
                ...state,
                transcript,
                currentStep: completed ? state.currentStep + 1 : state.currentStep,
                applicant: action.data.applicant ?? state.applicant,
            }

        case UPDATE_APPLICANT_SUCCESS:
            const iterateStep = Boolean(action.data.applicant.steps.find(({ id }) => (id === (action.input?.step ?? action.step)))?.completed);
            return {
                ...state,
                applicant: action.data.applicant,
                currentStep: state.currentStep + (iterateStep ? 1 : 0),
            }

        case ADD_BUSINESS_SUCCESS:
            return {
                ...state,
                business: state.business ? {
                    ...state.business,
                    businesses: [...state.business.businesses, action.data.business],
                } : { businesses: [action.data.business] },
            };

        case CHECK_BUSINESS_SIGNATURE_SUCCESS:
            if (!action.data.completed) {
                if (action.data.status === 'Declined') {
                    return {
                        ...state,
                        errorSteps: { ...state.errorSteps, business: true },
                    };
                }
                return state;
            }

            const updatedBusinesses = state.business?.businesses.map(b => ({
                ...b,
                signature: b.signature.map(s =>
                    s.transactionId === action.data.transactionId
                        ? { ...s, completed: true }
                        : s
                ),
                completed: b.id === action.input?.businessId
                    ? b.signature.every(s => s.transactionId === action.data.transactionId || s.completed)
                    : b.completed,
            })) ?? [];

            const allBusinessesComplete = updatedBusinesses.every(b => b.completed);
            const currentSharedSignature = state.business?.sharedSignature;
            const updatedSharedSignature: SigSignSignature | undefined = currentSharedSignature?.transactionId === action.data.transactionId && currentSharedSignature
                ? { ...currentSharedSignature, completed: true }
                : currentSharedSignature;

            return {
                ...state,
                business: state.business ? {
                    ...state.business,
                    businesses: updatedBusinesses as ApplicantBusiness[],
                    sharedSignature: updatedSharedSignature,
                } : undefined,
                currentStep: allBusinessesComplete ? state.currentStep + 1 : state.currentStep,
                applicant: action.data.applicant ?? state.applicant,
            };

        case CREATE_BUSINESS_SIGNATURE_SUCCESS:
            if (!state.business) return state;

            // Shared signature mode - no businessSignature provided
            if (action.data.businessIndex === undefined) {
                return {
                    ...state,
                    business: {
                        ...state.business,
                        sharedSignature: action.data.signature,
                    },
                };
            }

            // Per-business signature mode
            const businessesWithSignature = state.business.businesses.map((b, index) =>
                index === action.data.businessIndex
                    ? { ...b, signature: [...b.signature, action.data.signature] }
                    : b
            );

            return {
                ...state,
                business: {
                    ...state.business,
                    businesses: businessesWithSignature,
                },
            };

        default: return state;
    }
}

export interface GetApplicant {
    institutionId: string;
    applicantId?: string | null;
    accessToken?: string;
    refreshToken?: string;
    token?: string;
}

export interface PII<T = string> {
    name?: T;
    firstName?: T;
    lastName?: T;
    tin?: T;
    email?: T;
    addressLine1?: T;
    addressLine2?: T;
    city?: T;
    state?: T;
    postalCode?: T;
}

interface CreateApplicantBusiness {
    name?: string;
    ein?: string;
    email?: string;
    title?: string;
    address?: {
        lineOne?: string;
        lineTwo?: string;
        city?: string;
        state?: string;
        postalCode?: string;
    };
}

interface CreateApplicant {
    institutionId: string;
    applicantId: string;
    cId?: string;
    pii?: PII;
    encompassBorrowerId?: string;
    bundleId?: string;
    businesses?: CreateApplicantBusiness[];
}

export const signInUser = (payload: Payload<{ token: string; check: string; l: string; }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_APPLICANT_REQUEST,
        payload,
    });

    execute(get, '/auth', payload.input, payload.headers)
        .then(success(dispatch, GET_APPLICANT_SUCCESS, payload))
        .catch(failure(dispatch, GET_APPLICANT_FAILURE, payload));
};

export const getApplicant = (payload: Payload<GetApplicant>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_APPLICANT_REQUEST,
        payload,
    });

    execute(get, '/applicant', payload.input, payload.headers)
        .then(success(dispatch, GET_APPLICANT_SUCCESS, payload))
        .catch(failure(dispatch, GET_APPLICANT_FAILURE, payload));
};

export const verifyLast4Tin = (payload: Payload<{ institutionId: string; applicantId: string; last4Tin: string; }>) => (_dispatch: AppDispatch) => {
    execute(post, '/verify-tin', payload.input, payload.headers)
        .then((res) => { payload.onSuccess?.(res); })
        .catch(() => { payload.onFailure?.(undefined); });
};

export const createApplicant = (payload: Payload<CreateApplicant>) => (dispatch: AppDispatch) => {
    dispatch({
        type: CREATE_APPLICANT_REQUEST,
        payload,
    });

    execute(post, '/applicant', payload.input, payload.headers)
        .then(success(dispatch, CREATE_APPLICANT_SUCCESS, payload))
        .catch(failure(dispatch, CREATE_APPLICANT_FAILURE, payload));
}

export const getService = (payload: Payload<{ type: ConfigStepId }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_SERVICE_INFO_REQUEST,
        payload,
    });

    execute(get, '/service', payload.input, payload.headers)
        .then(success(dispatch, GET_SERVICE_INFO_SUCCESS, payload))
        .catch(failure(dispatch, GET_SERVICE_INFO_FAILURE, payload));
}

export const checkSignatureCompleted = (payload: Payload<{ transactionId: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_SIGNATURE_STATUS_REQUEST,
        payload,
    });

    execute(get, `/signature/${payload.input?.transactionId}`, undefined, payload.headers)
        .then(success(dispatch, GET_SIGNATURE_STATUS_SUCCESS, payload))
        .catch(failure(dispatch, GET_SIGNATURE_STATUS_FAILURE, payload));
}

export const addBasicInfo = (payload: Payload<{ pii: Partial<PII>; step: ConfigStepId; }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: UPDATE_APPLICANT_REQUEST,
        payload,
    });

    execute(patch, '/applicant', payload.input, payload.headers)
        .then(success(dispatch, UPDATE_APPLICANT_SUCCESS, payload))
        .catch(failure(dispatch, UPDATE_APPLICANT_FAILURE, payload));
}

export const seenWelcome = (payload: Payload<{ step: ConfigStepId; }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: UPDATE_APPLICANT_REQUEST,
        payload,
    });

    execute(patch, '/applicant', { seenWelcome: true, step: payload.input?.step }, payload.headers)
        .then(success(dispatch, UPDATE_APPLICANT_SUCCESS, payload))
        .catch(failure(dispatch, UPDATE_APPLICANT_FAILURE, payload));
}

export interface AddBusinessInput {
    name?: string;
    ein?: string;
    email?: string;
    title?: string;
    address?: {
        lineOne?: string;
        lineTwo?: string;
        city?: string;
        state?: string;
        postalCode?: string;
    };
}

export const addBusiness = (payload: Payload<AddBusinessInput>) => (dispatch: AppDispatch) => {
    dispatch({
        type: ADD_BUSINESS_REQUEST,
        payload,
    });

    execute(post, '/business', payload.input, payload.headers)
        .then(success(dispatch, ADD_BUSINESS_SUCCESS, payload))
        .catch(failure(dispatch, ADD_BUSINESS_FAILURE, payload));
}

export const checkBusinessSignatureCompleted = (payload: Payload<{
    transactionId: string;
    businessId?: string;
}>) => (dispatch: AppDispatch) => {
    dispatch({
        type: CHECK_BUSINESS_SIGNATURE_REQUEST,
        payload,
    });

    execute(get, `/signature/${payload.input?.transactionId}`, { businessId: payload.input?.businessId }, payload.headers)
        .then(success(dispatch, CHECK_BUSINESS_SIGNATURE_SUCCESS, payload))
        .catch(failure(dispatch, CHECK_BUSINESS_SIGNATURE_FAILURE, payload));
}

export const createBusinessSignature = (payload: Payload<{ businessIndex?: number }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: CREATE_BUSINESS_SIGNATURE_REQUEST,
        payload,
    });

    execute(post, '/business/signature', payload.input, payload.headers)
        .then(success(dispatch, CREATE_BUSINESS_SIGNATURE_SUCCESS, payload))
        .catch(failure(dispatch, CREATE_BUSINESS_SIGNATURE_FAILURE, payload));
}

export default reducer;
